import React from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { HTMLAsset, ImageAsset } from '@techstyle/react-assets';

import { trackMarketingCardClick } from '../../actions/products';
import { desktop, mobile, grid, getColWidth } from '../../styles';
import { decodeHTML } from '../../utils/helpers';
import { Component as AssetCTA } from '../AssetCTA';
import { EnterViewport } from '../EnterViewport';
import VipBoxButton from '../VipBoxButton/VipBoxButtonContainer';

const columnWillOverflow = (size, span, slot) => {
  // Note: This is inaccurate for all cases.
  // The problem is if an asset spans 2 slots, then the next remaining slot does not
  // adjust its position by +1, instead it remains as it was. This makes it tricky to
  // calculate whether the _second_ asset in a grid will overflow.
  // For now, this correctly adjusts the width for only the first asset.
  return slot % grid[size].columns === 0;
};

// Slots may need to be the same height as normal cells if appearing alongside
// them (as opposed to on their own entire row).
const cellDetailSpace = css`
  margin-bottom: 170px;
`;

const newRatioLeftCellSpace = css`
  margin-bottom: 165px;
`;

const newRatioRightCellSpace = css`
  margin-bottom: 180px;
`;

const getSlotStyles = (size, span, slot) => {
  const isEntireRow = span === 'fullWidth' || span >= grid[size].columns;
  const gapCount = span - 1;

  if (span === 'fullWidth') {
    return css`
      &:before {
        padding-bottom: 56px;
      }
    `;
  } else if (columnWillOverflow(size, span, slot)) {
    return css`
      &&[data-span='2'] {
        /* Override grid, force to 1 cell width. */
        width: ${getColWidth(size, 1)};
      }
      ${isEntireRow ? '' : cellDetailSpace};
      ${span === 1 || span === 2 ? newRatioRightCellSpace : ''}

      &:before {
        padding-bottom: 132.5%;
      }
    `;
  } else {
    return css`
      ${isEntireRow ? '' : cellDetailSpace};
      ${span === 1 ? newRatioLeftCellSpace : ''}
      ${span === 2 ? newRatioRightCellSpace : ''}

      &:before {
        padding-bottom: calc(
          (132.5% - ${grid[size].gap * gapCount}px) / ${span}
        );
      }

      @media screen and (max-width: 1023px) {
        margin-bottom: ${props => props.theme.spacing.medium}px;
      }
    `;
  }
};

const getImageStyles = (size, span, slot) => {
  if (columnWillOverflow(size, span, slot)) {
    return `display: none;`;
  }
};

const StyledAssetSlot = styled.div`
  display: flex;
  position: relative;

  background-color: ${props => props.bgColor};
  margin-bottom: ${props => props.marginBottomDesktop}px;

  ${mobile`
    margin-bottom: ${props => props.marginBottomMobile}px;
  `};

  ${grid.small.css`
    ${props => getSlotStyles('small', props.span, props.slot)};
  `};

  ${grid.medium.css`
    ${props => getSlotStyles('medium', props.span, props.slot)};
  `};

  ${grid.large.css`
    ${props => getSlotStyles('large', props.span, props.slot)};
  `};

  ${grid.xlarge.css`
    ${props => getSlotStyles('xlarge', props.span, props.slot)};
  `};

  /* Force the image asset to have a square ratio (1:1) */
  &:before {
    content: '';
    float: left;
    height: 0;
    margin-left: -1px;
    width: 1px;
  }

  &:after {
    content: '';
    clear: both;
    display: table;
  }
`;

/* The presence of this wrapper fixes a bug in FF 52 */
const AssetSlotContent = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-basis: ${props => (props.span ? `${100 / props.span}%` : `100%`)};
`;

const StyledImageAsset = styled(ImageAsset)`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: ${props => (props.span ? `${100 / props.span}%` : `100%`)};

  ${props =>
    props.bgType === 'backgroundContain' && `background-size: contain;`};

  ${grid.small.css`
    ${props => getImageStyles('small', props.span, props.slot)};
  `};

  ${grid.medium.css`
    ${props => getImageStyles('medium', props.span, props.slot)};
  `};

  ${grid.large.css`
    ${props => getImageStyles('large', props.span, props.slot)};
  `};

  ${grid.xlarge.css`
    ${props => getImageStyles('xlarge', props.span, props.slot)};
  `};
`;

const FixedImageAsset = styled(ImageAsset)`
  display: flex;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: ${props => (props.span ? `${100 / props.span}%` : `100%`)};
`;

const TextWrapper = styled.div`
  align-items: center;
  align-self: center;
  display: flex;
  flex-direction: column;
  padding: 0;
  text-align: center;
  width: 100%;

  ${mobile`
    padding: 0;
  `}
`;

const CtaWrapper = styled.div``;

const StyledHTMLAsset = styled(HTMLAsset)`
  margin-bottom: 1em;
  max-width: 330px;

  @media screen and (min-width: ${props => props.theme.breakpoints.large}px) {
    max-width: 610px;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.xxlarge}px) {
    max-width: 850px;
  }

  ${props =>
    props.theme.context.bca &&
    desktop`
      font-size: 16px;
      gap: 16px;

      p:has(strong) {
        margin-bottom: 24px;
      }

      strong, strong > span {
        ${({ theme }) => theme.header.variants.h2.textStyles}
        font-size: 24px !important;
      }

      p > span {
        ${({ theme }) => theme.paragraph.variants.paragraph2.textStyles}
        font-size: 16px !important;
      }

      h1 {
        font-size: 44px;
      }
      h2 {
        font-size: 32px;
      }
      h3 {
        font-size: 24px;
      }
   `};

  ${props =>
    props.theme.context.bca &&
    mobile`
      font-size: 12px;
      gap: 16px;

      p:has(strong) {
        margin-bottom: 24px;
      }

      strong, strong > span {
        ${({ theme }) => theme.header.variants.h2.textStyles}
        font-size: 24px !important;
      }

      p > span {
        ${({ theme }) => theme.paragraph.variants.paragraph2.textStyles}
        font-size: 16px !important;
      }

      h1 {
        font-size: 36px;
      }
      h2 {
        font-size: 24px;
      }
      h3 {
        font-size: 16px;
      }
    `};

  ${props => props.transparent && `background-color: transparent;`};
`;

const AssetSlotWrapper = ({ asset, children, onEnterViewport }) => {
  const {
    options: {
      customVars: {
        gridPosition,
        marginBottomDesktop,
        marginBottomMobile,
        slotCount,
      },
      ...options
    },
  } = asset;

  return (
    <StyledAssetSlot
      span={slotCount}
      slot={gridPosition}
      bgColor={options.bgColor || 'transparent'}
      data-span={slotCount}
      marginBottomDesktop={marginBottomDesktop || '64'}
      marginBottomMobile={marginBottomMobile || '40'}
    >
      <EnterViewport once onEnterViewport={onEnterViewport} />
      <AssetSlotContent>{children}</AssetSlotContent>
    </StyledAssetSlot>
  );
};

AssetSlotWrapper.propTypes = {
  asset: PropTypes.object.isRequired,
  children: PropTypes.node,
};

const AssetSlot = props => {
  const { children, onAssetCtaClick, ...rest } = props;
  const asset = rest.asset;

  const dispatch = useDispatch();

  if (!asset) {
    return null;
  }

  const {
    options: {
      customVars: {
        imagePosition,
        ctaUrl,
        ctaTheme,
        ctaText,
        slotCount,
        gridPosition,
        type = null,
      },
      htmlText,
    },
  } = asset;

  const postRegAssets =
    asset.container && asset.container.name
      ? asset.container.name.startsWith('quizresults')
      : null;
  const CustomImageAsset = !postRegAssets ? StyledImageAsset : FixedImageAsset;

  const shouldOpenModal = ctaUrl.startsWith('modal');

  const handleAssetCTAClick = () => {
    if (type === 'marketing') {
      dispatch(
        trackMarketingCardClick({
          eventAction: htmlText,
          eventLabel: gridPosition,
        })
      );
    }

    onAssetCtaClick && onAssetCtaClick();
  };

  const renderCta = shouldOpenModal ? (
    <VipBoxButton
      alternate={ctaTheme.includes('alternate')}
      secondary={ctaTheme.includes('secondary')}
      white={ctaTheme.includes('white')}
    >
      {decodeHTML(ctaText)}
    </VipBoxButton>
  ) : (
    <AssetCTA {...asset} onClick={handleAssetCTAClick} />
  );

  if (imagePosition.startsWith('background')) {
    return (
      <AssetSlotWrapper {...rest}>
        <CustomImageAsset background bgType={imagePosition} asset={asset}>
          <TextWrapper>
            <StyledHTMLAsset transparent asset={asset} />
            <CtaWrapper>{renderCta}</CtaWrapper>
          </TextWrapper>
        </CustomImageAsset>
      </AssetSlotWrapper>
    );
  }

  if (children) {
    return (
      <AssetSlotWrapper {...rest}>
        <CustomImageAsset background asset={asset}>
          <TextWrapper>
            <StyledHTMLAsset transparent asset={asset} />
            <div>
              {children}
              <CtaWrapper>{renderCta}</CtaWrapper>
            </div>
          </TextWrapper>
        </CustomImageAsset>
      </AssetSlotWrapper>
    );
  }

  return (
    <AssetSlotWrapper {...rest}>
      <CustomImageAsset
        span={slotCount}
        slot={gridPosition}
        background
        maintainAspectRatio
        asset={asset}
      >
        <div />
      </CustomImageAsset>
      <TextWrapper>
        <StyledHTMLAsset asset={asset} />
        <CtaWrapper>{renderCta}</CtaWrapper>
      </TextWrapper>
    </AssetSlotWrapper>
  );
};

AssetSlot.propTypes = {
  asset: PropTypes.object.isRequired,
  children: PropTypes.node,
  onAssetCtaClick: PropTypes.func,
  onEnterViewport: PropTypes.func,
};

AssetSlotWrapper.propTypes = AssetSlot.propTypes;

export default AssetSlot;
